import useClipboard from '@hooks/useClipboard';
import { useTruncate } from '@hooks/useTruncate';
import { useWindowSize } from '@hooks/useWindowSize';
import ContactEmergencyOutlinedIcon from '@mui/icons-material/ContactEmergencyOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Card, CircularProgress, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultipartUpload } from 'services/file-manager';
import { User, useUpdateUser, useUsersStore } from 'services/users';
import specialistImg from '../../../../assets/specialist.jpg';
import './styles.css';

interface UserCardProps {
	onEdit?: () => void;
	customUser?: any;
	showEdit?: boolean;
	children?: React.ReactNode;
	user?: User | null;
}

export const UserCard = ({ onEdit, children, showEdit = true, user: userProp }: UserCardProps) => {
	const value = 3.5;
	const { t } = useTranslation();
	const { user: storeUser } = useUsersStore();
	const { copied, copyToClipboard } = useClipboard();
	const { uploadFiles, resetStatus, isAllCompleted, isLoading } = useMultipartUpload();
	const mutate = useUpdateUser();
	const size = useWindowSize();
	const { truncate } = useTruncate();
	const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const user = userProp || storeUser;

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (uploadTimeoutRef.current) {
				clearTimeout(uploadTimeoutRef.current);
			}
		};
	}, []);

	const handleFileChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(event.target.files || []);

			// Clear any existing timeout
			if (uploadTimeoutRef.current) {
				clearTimeout(uploadTimeoutRef.current);
			}

			event.target.value = '';

			if (files.length > 0 && !isLoading) {
				uploadTimeoutRef.current = setTimeout(async () => {
					try {
						const uploadResults = await uploadFiles(files);

						if (uploadResults && uploadResults.length > 0 && uploadResults[0].key) {
							await mutate({
								profile_image: uploadResults[0].key,
							});
						}
					} catch (error) {
						console.error('Error uploading profile image:', error);
					}
				}, 300);
			}
		},
		[mutate, resetStatus, uploadFiles, isLoading]
	);

	const computedName = useMemo(() => {
		if (user?.company) {
			return {
				first_name: user?.company.company_name,
			};
		}

		return {
			first_name: user?.first_name,
			last_name: user?.last_name,
		};
	}, [user]);

	const handleViewResume = () => {
		window.open(`${process.env.REACT_APP_CDN_URL}${user?.user_files[0].s3_key}`, '_blank');
	};

	const handleEdit = (e: any) => {
		e.stopPropagation();
		onEdit && onEdit();
	};

	return (
		<>
			<Card
				sx={{
					borderRadius: '15px',
					boxShadow: '0px 2px 6px 0px #00000040',
					backgroundColor: 'white',
					padding: '20px',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					overflow: 'visible',
					'@media (max-width: 992px)': {
						flexDirection: 'column',
						position: 'relative',
						marginTop: '50px',
						justifyContent: 'center',
					},
					'@media (max-width: 576px)': {
						height: '292px',
					},
					'@media (max-width: 430px)': {
						height: '320px',
					},
				}}
				className="responsive-card"
			>
				{size.width <= 992 && (
					<div className="absolute top-[-34px]">
						<CardMedia
							sx={{
								width: '103px',
								height: '102px',
								borderRadius: '8px',
								boxShadow: '0px 2px 6px 0px #00000040',
							}}
							component="img"
							image={
								user?.profile_image
									? `${process.env.REACT_APP_CDN_URL}${user?.profile_image}`
									: specialistImg
							}
							alt="user"
						/>
						{isLoading && (
							<Box
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: '103px',
									height: '102px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: 'rgba(255, 255, 255, 0.7)',
									borderRadius: '8px',
								}}
							>
								<CircularProgress size={30} />
							</Box>
						)}
						{showEdit && (
							<>
								<input
									type="file"
									accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/heic, image/heif"
									style={{ display: 'none' }}
									id="upload-photo"
									onChange={handleFileChange}
								/>
								<label htmlFor="upload-photo">
									<IconButton
										sx={{
											borderRadius: '8px',
											width: '24px',
											height: '24px',
											boxShadow: '0px 2px 6px 0px #00000040',
											position: 'absolute',
											padding: '5px',
											background: '#fff',
											cursor: 'pointer',
											'&:hover': {
												backgroundColor: '#fff',
												cursor: 'pointer',
											},
										}}
										className="icon-position-responsive"
										component="span"
									>
										<CreateOutlinedIcon sx={{ color: '#00AB89', width: '14px', height: '14px' }} />
									</IconButton>
								</label>
							</>
						)}
					</div>
				)}
				<CardContent
					sx={{ display: 'flex', gap: '20px', alignItems: 'center', padding: '8px' }}
					className="max-lg:flex-col"
				>
					{size.width >= 992 && (
						<div className="relative">
							<CardMedia
								sx={{
									width: '103px',
									height: '102px',
									borderRadius: '8px',
									boxShadow: '0px 2px 6px 0px #00000040',
								}}
								component="img"
								image={
									user?.profile_image
										? `${process.env.REACT_APP_CDN_URL}${user?.profile_image}`
										: specialistImg
								}
								alt="user"
							/>
							{!isAllCompleted && (
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '103px',
										height: '102px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										backgroundColor: 'rgba(255, 255, 255, 0.7)',
										borderRadius: '8px',
									}}
								>
									<CircularProgress size={30} />
								</Box>
							)}
							{showEdit && (
								<>
									<input
										type="file"
										accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/heic, image/heif"
										style={{ display: 'none' }}
										id="upload-photo"
										onChange={handleFileChange}
									/>
									<label htmlFor="upload-photo">
										<IconButton
											sx={{
												borderRadius: '8px',
												width: '24px',
												height: '24px',
												boxShadow: '0px 2px 6px 0px #00000040',
												position: 'absolute',
												padding: '5px',
												background: '#fff',
												cursor: 'pointer',
												'&:hover': {
													backgroundColor: '#fff', // Override hover background
													cursor: 'pointer',
												},
											}}
											className="icon-position-responsive"
											component="span"
										>
											<CreateOutlinedIcon
												sx={{ color: '#00AB89', width: '14px', height: '14px' }}
											/>
										</IconButton>
									</label>
								</>
							)}
						</div>
					)}
					<div className="flex flex-col gap-[20px] pb-5 max-lg:gap-[10px] max-lg:mt-[40px] max-sm:!mt-[60px] ">
						<div className="flex justify-center lg:justify-start items-center">
							<Typography
								gutterBottom
								sx={{
									color: '#343A40',
									font: 'roboto-bold',
									fontSize: '20px !important',
									lineHeight: '140%',
									marginRight: '10px',
								}}
								component="div"
							>
								{truncate(`${computedName.first_name} ${computedName.last_name || ''} `, 18)}
							</Typography>
							{user?.is_active && <VerifiedIcon sx={{ color: '#00AB89' }} />}
							{showEdit && (
								<Button
									sx={{
										borderRadius: '8px',
										background: '#F8F9FA',
										height: '34px',
										padding: '5px 10px',
										alignItems: 'center',
										color: '#00AB89',
										fontFamily: 'Roboto',
										fontSize: '16px',
										fontStyle: 'normal',
										fontWeight: 600,
										lineHeight: '140%',
										marginLeft: '20px',
										boxShadow: 'unset',
										textTransform: 'capitalize',
									}}
									variant="contained"
									startIcon={<CreateOutlinedIcon />}
									onClick={onEdit}
								>
									{t('editable')}
								</Button>
							)}
						</div>
						{/* <div className="flex gap-[10px]">
							<Rating
								name="text-feedback"
								value={value}
								readOnly
								precision={0.5}
								emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
							/>
							<div className="text-dark font-roboto text-[16px] font-[600] leading-normal">
								{value}
							</div>
							<div className="text-secondary roboto-regular text-[16px] leading-normal underline cursor-pointer">
								23 {t('review')}
							</div>
						</div> */}
					</div>
				</CardContent>
				<CardActions className="responsive-buttons w-full sm:w-auto">
					{showEdit && (
						<Button
							sx={{
								borderRadius: '10px',
								border: '2px solid #00AB89',
								background: '#00AB89',
								width: size.width >= 640 ? '230px' : '100%',
								height: '44px',
								padding: '0px 10px',
								alignItems: 'center',
								color: '#F8FFFE',
								fontFamily: 'Roboto',
								fontSize: '16px',
								fontStyle: 'normal',
								fontWeight: 700,
								lineHeight: '140%',
								display: 'flex',
								justifyContent: 'center',
								position: 'relative',
							}}
							onClick={handleViewResume}
							variant="contained"
						>
							<ContactEmergencyOutlinedIcon />
							<span className="ml-[10px] mr-[40px]">{t('viewCV')}</span>
							{/* {showEdit && (
							<div
								className="flex justify-center items-center w-[40px] h-[100%] absolute right-[10px] border-l-2 border-[#038877] pl-[10px]"
								onClick={handleEdit}
							>
								<CreateOutlinedIcon />
							</div>
						)} */}
						</Button>
					)}

					<Button
						variant="outlined"
						sx={{
							borderRadius: '10px',
							border: '2px solid #00AB89',
							background: '#F8FFFE',
							width: size.width >= 640 ? '230px' : '100%',
							height: '44px',
							padding: '0px',
							alignItems: 'center',
							color: '#00AB89',
							fontFamily: 'Roboto',
							fontSize: '11px',
							fontStyle: 'normal',
							fontWeight: 600,
							lineHeight: '140%',
							display: 'flex',
							justifyContent: 'center',
							position: 'relative',
							marginLeft: '0 !important',
						}}
					>
						{copied ? (
							<div className="mr-[40px]">{t('copied')}</div>
						) : (
							<>
								<span>{showEdit ? t('myCode') : t('specialistCode')}:</span>
								<span className="font-[700] text-[16px] ml-[5px] mr-[35px]">
									{user?.connection_code}
								</span>
							</>
						)}
						<div
							onClick={() => copyToClipboard(user?.connection_code || '')}
							className="flex justify-center items-center w-[40px] h-[100%] absolute right-[10px] border-l-2 border-[#00AB89] pl-[10px]"
						>
							<ContentCopyOutlinedIcon />
						</div>
					</Button>
				</CardActions>
				{children}
			</Card>
		</>
	);
};
